/**
 * ============================================================
 * KEJJA OS — src/pages/finance/TransactionsPage.tsx
 * Full transactions list + add-transaction form.
 * ============================================================
 *
 * Built to work with just Card/Button/Badge from shadcn —
 * no Dialog/Input dependencies in case they aren't installed
 * yet. Uses native form elements styled to match.
 * ============================================================
 */

import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Plus, ArrowDownCircle, ArrowUpCircle, Trash2, X, Save,
} from 'lucide-react';

import { useFinanceContext } from './FinanceLayout';
import {
  useFinanceTransactions, useFinanceCategories, useFinanceAccounts,
  useCreateTransaction, useDeleteTransaction,
} from '@/hooks/useFinance';
import type { FinanceDirection, FinanceCategory } from '@/lib/financeApi';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { formatMoney, parseMoneyToMinor } from '@/lib/formatMoney';
import { cn } from '@/lib/utils';

export default function TransactionsPage() {
  const { homeId, currencyCode } = useFinanceContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterDirection, setFilterDirection] = useState<FinanceDirection | ''>('');
  const [filterCategoryId, setFilterCategoryId] = useState<string>('');
  const [showAdd, setShowAdd] = useState(searchParams.get('add') === '1');

  // Strip ?add=1 once opened
  useEffect(() => {
    if (searchParams.get('add') === '1') {
      const next = new URLSearchParams(searchParams);
      next.delete('add');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const { data: catsData } = useFinanceCategories(homeId);
  const categories = catsData?.categories ?? [];
  const catMap = new Map(categories.map((c) => [c.id, c]));

  const { data: txData, isLoading } = useFinanceTransactions(homeId, {
    direction: filterDirection || undefined,
    categoryId: filterCategoryId || undefined,
    limit: 200,
  });
  const transactions = txData?.transactions ?? [];

  const deleteMut = useDeleteTransaction(homeId);

  return (
    <div className="space-y-4">
      {/* ============ FILTERS BAR ============ */}
      <div className="flex flex-wrap items-center gap-2">
        <FilterChip
          label="All"
          active={!filterDirection && !filterCategoryId}
          onClick={() => { setFilterDirection(''); setFilterCategoryId(''); }}
        />
        <FilterChip
          label="Money in"
          active={filterDirection === 'in'}
          onClick={() => setFilterDirection(filterDirection === 'in' ? '' : 'in')}
        />
        <FilterChip
          label="Money out"
          active={filterDirection === 'out'}
          onClick={() => setFilterDirection(filterDirection === 'out' ? '' : 'out')}
        />

        {categories.length > 0 && (
          <select
            value={filterCategoryId}
            onChange={(e) => setFilterCategoryId(e.target.value)}
            className="h-8 rounded-lg border border-border bg-background text-sm px-2"
          >
            <option value="">All categories</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        )}

        <div className="ml-auto">
          <Button
            size="sm"
            onClick={() => setShowAdd(!showAdd)}
            className="bg-orange-600 hover:bg-orange-700 text-white"
          >
            {showAdd ? <X className="w-4 h-4 mr-1.5" /> : <Plus className="w-4 h-4 mr-1.5" />}
            {showAdd ? 'Close' : 'Add transaction'}
          </Button>
        </div>
      </div>

      {/* ============ ADD FORM ============ */}
      {showAdd && (
        <AddTransactionForm
          homeId={homeId}
          categories={categories}
          currencyCode={currencyCode}
          onDone={() => setShowAdd(false)}
        />
      )}

      {/* ============ LIST ============ */}
      {isLoading ? (
        <Card>
          <CardContent className="p-4 space-y-3">
            {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-12" />)}
          </CardContent>
        </Card>
      ) : transactions.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-8 text-center text-sm text-muted-foreground">
            No transactions match your filters.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0 divide-y divide-border">
            {transactions.map((tx) => {
              const cat = tx.categoryId ? catMap.get(tx.categoryId) : undefined;
              return (
                <div key={tx.id} className="px-4 py-3 flex items-center gap-3 hover:bg-muted/40 transition-colors group">
                  <div className={cn(
                    'h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0',
                    tx.direction === 'in'
                      ? 'bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300'
                      : 'bg-orange-100 dark:bg-orange-950/40 text-orange-700 dark:text-orange-300',
                  )}>
                    {tx.direction === 'in'
                      ? <ArrowDownCircle className="w-4 h-4" />
                      : <ArrowUpCircle className="w-4 h-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm text-foreground truncate max-w-[200px]">
                        {tx.merchant ?? tx.description ?? 'Transaction'}
                      </span>
                      {cat && (
                        <Badge
                          variant="outline"
                          className="text-[10px] font-normal"
                          style={cat.color ? { borderColor: cat.color, color: cat.color } : {}}
                        >
                          {cat.name}
                        </Badge>
                      )}
                      {tx.source === 'mpesa_sms' && (
                        <Badge variant="outline" className="text-[10px] font-normal bg-emerald-50 dark:bg-emerald-950/20 border-emerald-300 text-emerald-700 dark:text-emerald-300">
                          M-Pesa
                        </Badge>
                      )}
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-2">
                      <span>{new Date(tx.occurredAt).toLocaleDateString()}</span>
                      {tx.reference && (
                        <span className="font-mono">· {tx.reference}</span>
                      )}
                    </div>
                  </div>
                  <span className={cn(
                    'text-sm font-semibold tabular-nums flex-shrink-0',
                    tx.direction === 'in' ? 'text-emerald-600 dark:text-emerald-400' : 'text-foreground',
                  )}>
                    {tx.direction === 'in' ? '+' : '−'}{formatMoney(tx.amount, tx.currencyCode)}
                  </span>
                  <button
                    onClick={() => {
                      if (confirm('Delete this transaction?')) deleteMut.mutate(tx.id);
                    }}
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded hover:bg-rose-100 dark:hover:bg-rose-950/40 text-muted-foreground hover:text-rose-600"
                    aria-label="Delete"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      <div className="text-[11px] text-muted-foreground text-center pt-2">
        Showing {transactions.length} transaction{transactions.length === 1 ? '' : 's'}
      </div>
    </div>
  );
}

/* ============ Add transaction form ============ */

function AddTransactionForm({
  homeId, categories, currencyCode, onDone,
}: {
  homeId: string;
  categories: FinanceCategory[];
  currencyCode: string;
  onDone: () => void;
}) {
  const { data: acctData } = useFinanceAccounts(homeId);
  const accounts = acctData?.accounts ?? [];
  const mutation = useCreateTransaction(homeId);

  const [direction, setDirection] = useState<FinanceDirection>('out');
  const [amount, setAmount] = useState('');
  const [merchant, setMerchant] = useState('');
  const [description, setDescription] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [accountId, setAccountId] = useState('');
  const [occurredAt, setOccurredAt] = useState(new Date().toISOString().slice(0, 10));
  const [err, setErr] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    const minor = parseMoneyToMinor(amount, currencyCode);
    if (minor === null || minor <= 0) {
      setErr('Enter a valid amount.');
      return;
    }
    mutation.mutate({
      direction,
      amount: minor,
      currencyCode,
      merchant: merchant.trim() || null,
      description: description.trim() || null,
      categoryId: categoryId || null,
      accountId: accountId || null,
      occurredAt: new Date(occurredAt).toISOString(),
    }, {
      onSuccess: () => {
        setAmount(''); setMerchant(''); setDescription('');
        onDone();
      },
      onError: (e: Error) => setErr(e.message ?? 'Could not save.'),
    });
  };

  const inputCls = 'w-full h-9 rounded-lg border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500';

  return (
    <Card className="border-orange-200 dark:border-orange-900/50 bg-gradient-to-br from-orange-50/40 to-transparent dark:from-orange-950/10">
      <CardContent className="p-4">
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setDirection('out')}
              className={cn(
                'flex-1 h-9 rounded-lg border text-sm font-medium transition-colors',
                direction === 'out'
                  ? 'bg-orange-600 text-white border-orange-600'
                  : 'bg-background text-muted-foreground border-border hover:bg-muted',
              )}
            >
              Money out
            </button>
            <button
              type="button"
              onClick={() => setDirection('in')}
              className={cn(
                'flex-1 h-9 rounded-lg border text-sm font-medium transition-colors',
                direction === 'in'
                  ? 'bg-emerald-600 text-white border-emerald-600'
                  : 'bg-background text-muted-foreground border-border hover:bg-muted',
              )}
            >
              Money in
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                Amount ({currencyCode})
              </label>
              <input
                type="text"
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className={inputCls}
                autoFocus
                required
              />
            </div>
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                Date
              </label>
              <input
                type="date"
                value={occurredAt}
                onChange={(e) => setOccurredAt(e.target.value)}
                className={inputCls}
              />
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
              Merchant / Who
            </label>
            <input
              type="text"
              value={merchant}
              onChange={(e) => setMerchant(e.target.value)}
              placeholder="e.g. Naivas, matatu, school fees"
              className={inputCls}
            />
            <p className="text-[10px] text-muted-foreground mt-1">
              Leave category empty and we'll auto-categorize based on the merchant.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                Category
              </label>
              <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} className={inputCls}>
                <option value="">Auto (from merchant)</option>
                {categories
                  .filter((c) => direction === 'in' ? c.kind === 'income' : c.kind !== 'income')
                  .map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                Account
              </label>
              <select value={accountId} onChange={(e) => setAccountId(e.target.value)} className={inputCls}>
                <option value="">(none)</option>
                {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
              Note (optional)
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="A short note for your records"
              className={inputCls}
            />
          </div>

          {err && (
            <div className="text-xs text-rose-600 dark:text-rose-400 font-medium">{err}</div>
          )}

          <div className="flex gap-2 pt-1">
            <Button
              type="submit"
              disabled={mutation.isPending}
              className="bg-orange-600 hover:bg-orange-700 text-white flex-1"
            >
              <Save className="w-4 h-4 mr-1.5" />
              {mutation.isPending ? 'Saving…' : 'Save transaction'}
            </Button>
            <Button type="button" variant="outline" onClick={onDone}>
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function FilterChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'h-8 px-3 rounded-lg text-sm font-medium transition-colors border',
        active
          ? 'bg-orange-100 dark:bg-orange-950/40 text-orange-700 dark:text-orange-300 border-orange-300 dark:border-orange-800'
          : 'bg-background text-muted-foreground border-border hover:bg-muted hover:text-foreground',
      )}
    >
      {label}
    </button>
  );
}
